// AdminDashboardPage.jsx - Growth and revenue metrics dashboard for admins
import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import logger from "../../utils/logger";

function StatCard({ label, value, sub, highlight }) {
  return (
    <div
      style={{
        padding: "1.25rem",
        borderRadius: "8px",
        border: highlight
          ? "2px solid var(--primary-color, #1976d2)"
          : "1px solid var(--border-color, #ddd)",
        background: "var(--bg-secondary, #fafafa)",
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
      }}
    >
      <div
        style={{
          fontSize: "0.8rem",
          color: "var(--text-secondary, #666)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.75rem",
          fontWeight: "700",
          color: highlight
            ? "var(--primary-color, #1976d2)"
            : "var(--text-primary, #333)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: "0.78rem", color: "var(--text-secondary, #888)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h3
      style={{
        margin: "1.75rem 0 0.75rem",
        color: "var(--text-primary, #333)",
        fontSize: "1rem",
        fontWeight: "600",
        borderBottom: "1px solid var(--border-color, #ddd)",
        paddingBottom: "0.4rem",
      }}
    >
      {children}
    </h3>
  );
}

function pct(rate) {
  if (rate === null || rate === undefined) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

function money(n, currency = "USD") {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}

function AdminDashboardPage({ _user }) {
  const [metrics, setMetrics] = useState(null);
  const [retention, setRetention] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/dashboard/metrics`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMetrics(await res.json());
    } catch (err) {
      logger.error("Dashboard metrics fetch failed:", err);
      setError("Failed to load dashboard metrics");
    }
  }, []);

  const fetchRetention = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/dashboard/retention`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      setRetention(await res.json());
    } catch (err) {
      logger.error("Dashboard retention fetch failed:", err);
    }
  }, []);

  const fetchRevenue = useCallback(async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/dashboard/revenue`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      setRevenue(await res.json());
    } catch (err) {
      logger.error("Dashboard revenue fetch failed:", err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchMetrics(), fetchRetention(), fetchRevenue()]).finally(() =>
      setLoading(false)
    );
  }, [fetchMetrics, fetchRetention, fetchRevenue]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await httpRequest(`${API_BASE}/admin/dashboard/export`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `pista_users_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("Dashboard export failed:", err);
    } finally {
      setExporting(false);
    }
  }, []);

  return (
    <div className="admin-section" data-testid="admin-dashboard-page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.75rem",
          marginBottom: "0.5rem",
        }}
      >
        <h2 style={{ margin: 0 }}>Growth Dashboard</h2>
        <button
          className="admin-btn admin-btn-primary"
          onClick={handleExport}
          disabled={exporting}
          data-testid="export-csv-btn"
          style={{ whiteSpace: "nowrap" }}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {loading && (
        <p style={{ color: "var(--text-secondary, #666)" }}>Loading…</p>
      )}
      {error && (
        <p style={{ color: "var(--error-color, #d32f2f)" }}>{error}</p>
      )}

      {metrics && (
        <>
          <SectionHeading>Users</SectionHeading>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "1rem",
            }}
          >
            <StatCard
              label="Total Registered"
              value={fmt(metrics.total_users)}
              highlight
            />
            <StatCard
              label="New Today"
              value={fmt(metrics.new_users_today)}
            />
            <StatCard
              label="Activation Rate (7d)"
              value={pct(metrics.activation_rate_7d?.rate)}
              sub={`${fmt(metrics.activation_rate_7d?.activated)} / ${fmt(
                metrics.activation_rate_7d?.registered
              )} registered`}
            />
            <StatCard
              label="Activation Rate (30d)"
              value={pct(metrics.activation_rate_30d?.rate)}
              sub={`${fmt(metrics.activation_rate_30d?.activated)} / ${fmt(
                metrics.activation_rate_30d?.registered
              )} registered`}
            />
            <StatCard
              label="7-day Retention"
              value={pct(metrics.retention_7d?.rate)}
              sub={`${fmt(metrics.retention_7d?.returned)} / ${fmt(
                metrics.retention_7d?.cohort_size
              )} cohort`}
            />
            <StatCard
              label="Session Log Rate"
              value={pct(metrics.session_log_rate)}
              sub="session_logged / activated"
            />
          </div>
        </>
      )}

      {retention && (
        <>
          <SectionHeading>Retention Cohorts</SectionHeading>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
            }}
          >
            <StatCard
              label="7-day Retention"
              value={pct(retention.retention_7d?.rate)}
              sub={`Cohort: ${fmt(retention.retention_7d?.cohort_size)} users from 7–14 days ago`}
            />
            <StatCard
              label="30-day Retention"
              value={pct(retention.retention_30d?.rate)}
              sub={`Cohort: ${fmt(retention.retention_30d?.cohort_size)} users from 30–60 days ago`}
            />
          </div>
        </>
      )}

      {(metrics || revenue) && (
        <>
          <SectionHeading>Revenue</SectionHeading>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "1rem",
            }}
          >
            <StatCard
              label="Paid Users"
              value={fmt(revenue?.paid_users ?? metrics?.paid_users)}
            />
            <StatCard
              label="Revenue YTD"
              value={money(revenue?.revenue_ytd ?? metrics?.revenue_ytd)}
              highlight
            />
            <StatCard
              label="ARPU"
              value={money(revenue?.arpu ?? metrics?.arpu)}
              sub="revenue YTD / total users"
            />
          </div>

          {revenue?.monthly_breakdown?.length > 0 && (
            <>
              <SectionHeading>Monthly Revenue Breakdown</SectionHeading>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>New Paid Users</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.monthly_breakdown.map((row) => (
                      <tr key={row.month}>
                        <td>{row.month}</td>
                        <td>{fmt(row.new_paid)}</td>
                        <td>{money(row.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AdminDashboardPage;
